import { convertPathMmToPx, mmToPx } from '../../shared/units'
import type { CreateDesignResponse } from '../../../infrastructure/api/designApi'
import { calculateHeightFromWidth, getImageDimensions } from './imageService'

/**
 * Zone Calculator Service
 * Handles zone dimension calculations and overrides
 */

export type ZoneOverrides = {
  background?: { width: number; height: number }
  // export.clipPath is the bleed contour: the design clipPath offset outward by the
  // bleed margin (e.g. 3mm). Print extends to this contour and is trimmed at the design clipPath.
  export: { top: number; left: number; width: number; height: number; clipPath?: string }
  design: { top: number; left: number; width: number; height: number; clipPath?: string }
}

export type CalculatedZones = {
  background: { width: number; height: number }
  export: { top: number; left: number; width: number; height: number; clipPath?: string }
  design: { top: number; left: number; width: number; height: number; clipPath?: string }
}

export const calculateZones = async (
  zones: CreateDesignResponse['zones'],
  backgroundImageUrl: string,
  overrides?: ZoneOverrides | null,
): Promise<CalculatedZones> => {
  let backgroundHeightMm: number
  const backgroundWidthMm = overrides?.background?.width || zones.background.width

  try {
    // Try to get image dimensions to calculate aspect ratio
    const imageDimensions = await getImageDimensions(backgroundImageUrl)
    backgroundHeightMm = calculateHeightFromWidth(backgroundWidthMm, imageDimensions.aspectRatio)
  } catch (error) {
    console.warn('Failed to load background image, using default aspect ratio', error)
    // Use a default aspect ratio of 1.414 (roughly A4 paper ratio) as fallback
    const defaultAspectRatio = 1.414
    backgroundHeightMm = calculateHeightFromWidth(backgroundWidthMm, defaultAspectRatio)
  }

  const exportZone = overrides?.export || zones.export
  const designZone = overrides?.design || zones.design

  // Convert export zone to pixels (relative to background)
  const exportPx = {
    top: mmToPx(exportZone.top),
    left: mmToPx(exportZone.left),
    width: mmToPx(exportZone.width),
    height: mmToPx(exportZone.height),
    ...(exportZone.clipPath && { clipPath: convertPathMmToPx(exportZone.clipPath) }),
  }

  // Convert design zone to pixels and make it relative to export zone
  const designPx = {
    top: exportPx.top + mmToPx(designZone.top),
    left: exportPx.left + mmToPx(designZone.left),
    width: mmToPx(designZone.width),
    height: mmToPx(designZone.height),
    ...(designZone.clipPath && { clipPath: convertPathMmToPx(designZone.clipPath) }),
  }

  return {
    background: {
      width: mmToPx(backgroundWidthMm),
      height: mmToPx(backgroundHeightMm),
    },
    export: exportPx,
    design: designPx,
  }
}

export const calculateCanvasScale = (
  containerWidth: number,
  containerHeight: number,
  backgroundWidth: number,
  backgroundHeight: number,
  padding: number = 40,
): number => {
  // Validate inputs
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    backgroundWidth <= 0 ||
    backgroundHeight <= 0
  ) {
    return 1
  }

  const availableWidth = containerWidth - padding * 2
  const availableHeight = containerHeight - padding * 2

  // Ensure we have positive available space
  if (availableWidth <= 0 || availableHeight <= 0) {
    return 0.1 // Minimum scale
  }

  const scaleX = availableWidth / backgroundWidth
  const scaleY = availableHeight / backgroundHeight
  const scale = Math.min(scaleX, scaleY)

  // Ensure scale is finite and reasonable
  if (!isFinite(scale) || scale <= 0) {
    return 1
  }

  // Cap scale to reasonable limits
  return Math.min(Math.max(scale, 0.1), 10)
}
